import { Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { v4 } from 'uuid';

import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { BillingPreviewDomainHeader } from 'src/routes/payments/components/preview/BillingPreviewDomainHeader';
import {
  BillingPreviewHeader,
  PreviewMode,
} from 'src/routes/payments/components/preview/BillingPreviewHeader';
import { ProductStorePage } from 'src/routes/payments/store/components/ProductStore';
import { ActiveFormSectionType } from 'src/routes/payments/store/form/components/ProductStoreEditor';
import {
  ProductStoreLineItem,
  ProductStoreSchema,
  ProductStoreVisibility,
} from 'src/routes/payments/store/types/schema';
import {
  Price,
  PriceType,
  Product,
  ProductStatus,
} from 'src/services/api/productsApi';

const STORE_NAME = 'Store name';

interface Props {
  basePortalDomain: ReturnType<typeof useCustomDomains>['basePortalDomain'];
  productStore: ProductStoreSchema;
  activeFormSection: ActiveFormSectionType | null;
  products: Product[];
  prices: Price[];
}

export const ProductStorePreview: React.FC<Props> = ({
  basePortalDomain,
  productStore,
  activeFormSection,
  products = [],
  prices = [],
}) => {
  const [previewMode, setPreviewMode] = React.useState(PreviewMode.Desktop);

  const handleChangePreviewMode = (mode: PreviewMode) => {
    setPreviewMode(mode);
  };

  const formattedProductStore = useMemo(() => {
    const getFormattedProduct = (item: ProductStoreLineItem): Product => ({
      ...item,
      id: item.id || v4(),
      name: products.find((p) => p.id === item.productId)?.name || '',
      createdAt: '',
      updatedAt: '',
      imageUrls: [],
      status: ProductStatus.ACTIVE,
      taxPercentage: 0,
      priceIds: [],
      isUsed: false,
    });

    const getFormattedPrice = (
      item: ProductStoreLineItem,
      type: PriceType,
    ): Price => ({
      ...item,
      id: item.id || v4(),
      amount: Number(item.amount) * 100,
      currency: '',
      createdAt: '',
      ref: '',
      isUsed: false,
      interval: prices.find((p) => p.id === item.priceId)?.interval,
      type,
      productId: item.productId || '',
    });

    const formatItems = (items: ProductStoreLineItem[], type: PriceType) =>
      items.map((item) => ({
        ...item,
        description: item.description || '',
        product: getFormattedProduct(item),
        price: getFormattedPrice(item, type),
      }));

    return {
      ...productStore,
      id: productStore.id || '',
      displayName: productStore.displayName,
      ref: v4(),
      subscriptions: formatItems(
        productStore.subscriptions,
        PriceType.Recurring,
      ),
      oneTimeProducts: formatItems(
        productStore.oneTimeProducts,
        PriceType.OneTime,
      ),
    };
  }, [productStore, products, prices]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col items-center w-full @lg:max-w-[1440px] ">
        <BillingPreviewHeader
          previewMode={previewMode}
          onChangePreviewMode={handleChangePreviewMode}
        />
        <div
          className={clsx(
            'w-full transition-all  duration-400 ease-in-out @container rounded-lg border border-solid border-non-hover-border bg-white',
            {
              'max-w-full': previewMode === PreviewMode.Desktop,
              'max-w-[375px]': previewMode === PreviewMode.Mobile,
            },
          )}
        >
          <BillingPreviewDomainHeader
            portalDomainUrl={`${basePortalDomain}/store`}
            showCopyIcon={
              productStore.productStorePreferences.visibility ===
                ProductStoreVisibility.All ||
              productStore.productStorePreferences.visibility ===
                ProductStoreVisibility.Public
            }
          />
          <div
            className={clsx({
              'relative before:content-[""] before:absolute before:top-0 before:left-0 before:h-full before:w-[3px] before:bg-purple-200':
                activeFormSection === ActiveFormSectionType.DisplayName,
              'before:hidden':
                activeFormSection === ActiveFormSectionType.Subscriptions ||
                activeFormSection === ActiveFormSectionType.OneTimeProducts,
            })}
          >
            <div className="py-3 px-6">
              <Heading
                size="sm"
                className={clsx({
                  'text-light-gray': !productStore.displayName,
                })}
              >
                {productStore.displayName || STORE_NAME}
              </Heading>
            </div>
            <div className="border-b border-solid border-t-0 border-x-0 border-gray-200 w-full" />
          </div>
          <div className=" h-[calc(100vh-450px)] @xl:h-[calc(100vh-250px)] block @sm:flex pb-[60px] @sm:pb-0 relative overflow-auto [scrollbar-gutter:stable]">
            <div className="w-full">
              <ProductStorePage
                productStore={formattedProductStore}
                onClickPurchase={() => Promise.resolve()}
                isMobilePreview={previewMode === PreviewMode.Mobile}
                isPreview
                activeFormSection={activeFormSection}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
